import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiMenu, FiX, FiChevronDown, FiSun, FiMoon } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const isHome = location.pathname === '/';
  const { dark, toggle } = useTheme();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id) => {
    setIsOpen(false);
    setShowDropdown(false);
    if (!isHome) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const departments = [
    { id: 'klassiker', name: 'Klassiker' },
    { id: 'demografie', name: 'Demografie' },
    { id: 'kinder', name: 'Kinder & Jugend' }
  ];

  return (
    <nav className="bg-white dark:bg-navy-900 shadow-sm dark:shadow-navy-800 sticky top-0 z-50 border-b border-stone-100 dark:border-navy-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-3 text-2xl font-serif font-bold text-brand dark:text-amber-400">
              <img src="/logo_vidento.png" alt="Vidento Logo" className="h-10 w-10 object-contain" />
              Vidento
            </Link>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-8 text-sm font-semibold">
            <button
              onClick={() => scrollToSection('verlag')}
              className="text-stone-600 dark:text-stone-300 hover:text-amber-700 dark:hover:text-amber-400 transition-colors"
            >
              Der Verlag
            </button>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                onMouseEnter={() => setShowDropdown(true)}
                className="flex items-center gap-1 text-stone-600 dark:text-stone-300 hover:text-amber-700 dark:hover:text-amber-400 transition-colors py-8"
              >
                Abteilungen <SafeIcon icon={FiChevronDown} className={`transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showDropdown && (
                <div
                  className="absolute top-full left-0 w-56 bg-white dark:bg-navy-800 border border-stone-100 dark:border-navy-700 shadow-xl rounded-b-md overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                  onMouseLeave={() => setShowDropdown(false)}
                >
                  {departments.map((dept) => (
                    <Link
                      key={dept.id}
                      to={`/abteilung/${dept.id}`}
                      onClick={() => setShowDropdown(false)}
                      className="block px-6 py-4 text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-navy-700 hover:text-amber-800 dark:hover:text-amber-400 border-b border-stone-50 dark:border-navy-700 last:border-none transition-colors"
                    >
                      {dept.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/autoren" className="text-stone-600 dark:text-stone-300 hover:text-amber-700 dark:hover:text-amber-400 transition-colors">Autoren</Link>
            <Link to="/kontakt" className="text-stone-600 dark:text-stone-300 hover:text-amber-700 dark:hover:text-amber-400 transition-colors">Kontakt</Link>

            {/* Dark mode toggle */}
            <button
              onClick={toggle}
              aria-label="Dark Mode umschalten"
              className="w-9 h-9 flex items-center justify-center rounded-full border border-stone-200 dark:border-navy-700 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-navy-800 transition-colors"
            >
              {dark
                ? <SafeIcon icon={FiSun} className="w-4 h-4 text-amber-400" />
                : <SafeIcon icon={FiMoon} className="w-4 h-4" />
              }
            </button>
          </div>

          {/* Mobile: toggle + hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={toggle}
              aria-label="Dark Mode umschalten"
              className="w-9 h-9 flex items-center justify-center rounded-full border border-stone-200 dark:border-navy-700 text-stone-500 dark:text-stone-400"
            >
              {dark
                ? <SafeIcon icon={FiSun} className="w-4 h-4 text-amber-400" />
                : <SafeIcon icon={FiMoon} className="w-4 h-4" />
              }
            </button>
            <button onClick={() => setIsOpen(!isOpen)} className="text-stone-600 dark:text-stone-300 hover:text-stone-900 focus:outline-none">
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-7 w-7" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-white dark:bg-navy-900 border-t border-stone-100 dark:border-navy-800 shadow-inner">
          <div className="px-4 pt-4 pb-6 space-y-2">
            <button
              onClick={() => scrollToSection('verlag')}
              className="block w-full text-left px-3 py-3 text-lg font-serif font-bold text-brand dark:text-amber-400 border-b border-stone-50 dark:border-navy-800"
            >
              Der Verlag
            </button>
            <div className="py-2">
              <div className="px-3 py-2 text-xs font-bold text-stone-400 dark:text-stone-500 tracking-widest">Unsere Abteilungen</div>
              <div className="grid grid-cols-1 gap-1 mt-2">
                {departments.map((dept) => (
                  <Link
                    key={dept.id}
                    to={`/abteilung/${dept.id}`}
                    onClick={() => setIsOpen(false)}
                    className="block px-6 py-3 text-stone-600 dark:text-stone-300 hover:bg-amber-50 dark:hover:bg-navy-800 rounded-md transition-colors"
                  >
                    {dept.name}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/autoren"
              onClick={() => setIsOpen(false)}
              className="block w-full text-left px-3 py-3 text-lg font-serif font-bold text-brand dark:text-amber-400 border-t border-stone-50 dark:border-navy-800"
            >
              Autoren
            </Link>
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="block w-full text-left px-3 py-3 text-lg font-serif font-bold text-brand dark:text-amber-400 border-t border-stone-50 dark:border-navy-800"
            >
              Kontakt
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
